import { useEffect, useMemo, useState } from "react";

import { useForm } from "react-hook-form";

import { ExclamationTriangleIcon, MagnifyingGlassIcon, PencilSquareIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";

import Modal from "../../layouts/Modal";
import PickAddress from "../shared/Addresses/PickAddress";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import { CheckboxInput } from "../shared/Inputs/CheckboxInput";
import { TextInput } from "../shared/Inputs/TextInput";
import CreateVehicleModal from "./CreateVehicleModal";
import { IVehicle } from "../../models/Vehicle";

interface LeadVehiclesTableProps {
  vehicles: any[];
  register: any;
  setValue: any;
  getValues: any;
  watch: any;
  remove: (index: number) => void;
  handleVrmLookup: (index: number) => void;
  isSearching: boolean[];
  createVehicleOpen: boolean;
  setCreateVehicleOpen: any;
}

// Address modal component for editing a vehicle's address
function VehicleAddressModal({ open, onClose, address, onSave }: { open: boolean, onClose: any, address: any, onSave: (address: any) => void }) {
  const { register, setValue, watch, getValues } = useForm({ defaultValues: address });

  const handleSave = () => {
    onSave(getValues());
    onClose();
  };

  return (
    <Modal open={open} setOpen={onClose} width="max-w-3xl">
      <div className="p-4">
        <div className="mb-4 font-semibold">Edit Address</div>
        <PickAddress register={register} setValue={setValue} watch={watch} />
        <div className="flex gap-2 mt-4">
          <ButtonInput label="Save" onClick={handleSave} isSubmit={false} classes="" />
          <ButtonInput label="Cancel" onClick={onClose} isSubmit={false} classes="!bg-gray-300 hover:!bg-gray-200 !text-gray-700" />
        </div>
      </div>
    </Modal>
  );
}

export default function LeadVehiclesTable({ vehicles, register, setValue, getValues, watch, remove, handleVrmLookup, isSearching, createVehicleOpen, setCreateVehicleOpen }: LeadVehiclesTableProps) {

  const [openAddressModal, setOpenAddressModal] = useState<number | null>(null);

  const [miscVehicle, setMiscVehicle] = useState<IVehicle | undefined>();

  useEffect(() => {
    if (miscVehicle) {
      setValue(`vehicles.${vehicles.length - 1}.vrm`, miscVehicle.vrm);
      setValue(`vehicles.${vehicles.length - 1}.make`, miscVehicle.dvlaMake);
      setValue(`vehicles.${vehicles.length - 1}.model`, miscVehicle.dvlaModel);
    }
  }, [miscVehicle]);

  const columns = useMemo(
    () => [
      {
        header: "VRM/VIN",
        accessorKey: "vrm",
        cell: ({ row }: any) => (
          <div className="relative mb-[3px]">
            <TextInput
              register={register}
              registerName={`vehicles.${row.index}.vrm`}
              label=""
              customClasses="min-w-[100px]"
            />
          </div>
        ),
      },
      {
        header: "",
        id: "search",
        cell: ({ row }: any) => (
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="bg-primary-600 hover:bg-primary-700 text-white rounded p-2 flex items-center justify-center disabled:opacity-50"
              onClick={() => handleVrmLookup(row.index)}
              disabled={isSearching[row.index]}
              aria-label="Search VRM"
            >
              {isSearching[row.index] ? (
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="4" fill="none" />
                  <path d="M4 12a8 8 0 018-8" stroke="white" strokeWidth="4" fill="none" />
                </svg>
              ) : (
                <MagnifyingGlassIcon className="h-5 w-5" />
              )}
            </button>

            <button
              type="button"
              className="bg-primary-600 hover:bg-primary-700 text-white rounded p-2 flex items-center justify-center disabled:opacity-50"
              onClick={() => setCreateVehicleOpen(true)}
              disabled={isSearching[row.index]}
              aria-label="Create without VRM"
            >
              {isSearching[row.index] ? (
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="4" fill="none" />
                  <path d="M4 12a8 8 0 018-8" stroke="white" strokeWidth="4" fill="none" />
                </svg>
              ) : (
                <PencilSquareIcon className="h-5 w-5" />
              )}
            </button>
          </div>
        ),
      },
      {
        header: "Make",
        accessorKey: "make",
        cell: ({ row }: any) => (
          <TextInput
            register={register}
            registerName={`vehicles.${row.index}.make`}
            label=""
            customClasses="min-w-[100px]"
          />
        ),
      },
      {
        header: "Model",
        accessorKey: "model",
        cell: ({ row }: any) => (
          <TextInput
            register={register}
            registerName={`vehicles.${row.index}.model`}
            label=""
            customClasses="min-w-[100px]"
          />
        ),
      },
      {
        header: "Address",
        id: "address",
        cell: ({ row }: any) => {
          const address = watch(`vehicles.${row.index}.address`) || {};
          const isDropOff = watch(`vehicles.${row.index}.isDropOff`) || false;
          const noAddress = (!address.postcode || !address.line1) && !isDropOff;

          return (
            <div className={`${noAddress ? "bg-red-200" : ""} flex items-center gap-2 min-w-[250px]`}>
              {!isDropOff && (
                noAddress ? (
                  <>
                    <span><ExclamationTriangleIcon className="h-5 w-5 text-red-500" /></span>
                    <span>No Address</span>
                  </>
                ) : (
                  <span>{[address.line1, address.postcode].filter(Boolean).join(", ")}</span>
                )
              )}
              <CheckboxInput register={register} registerName={`vehicles.${row.index}.isDropOff`} label="Drop Off" />
              {!isDropOff && (
                <button
                  type="button"
                  className="ml-2 text-primary-600 underline text-xs"
                  onClick={() => setOpenAddressModal(row.index)}
                >
                  Edit
                </button>
              )}
              {isDropOff && (
                <CheckboxInput register={register} registerName={`vehicles.${row.index}.alreadyOnSite`} label="Already On Site" />
              )}
              {openAddressModal === row.index && (
                <VehicleAddressModal
                  open={true}
                  onClose={setOpenAddressModal}
                  address={address}
                  onSave={(newAddress) => setValue(`vehicles.${row.index}.address`, newAddress)}
                />
              )}
            </div>
          );
        },
      },
      {
        header: "Commission",
        accessorKey: "commission",
        cell: ({ row }: any) => (
          <TextInput
            register={register}
            registerName={`vehicles.${row.index}.commission`}
            label=""
            customClasses="min-w-[80px]"
            type="number"
            initialValue={"0"}
          />
        ),
      },
      {
        header: "Price",
        accessorKey: "price",
        cell: ({ row }: any) => (
          <TextInput
            register={register}
            registerName={`vehicles.${row.index}.price`}
            label=""
            customClasses="min-w-[80px]"
            type="number"
            step=".01"
            initialValue={"0"}
          />
        ),
      },
      {
        header: "Seller charged VAT",
        accessorKey: "applyVAT",
        cell: ({ row }: any) => (
          <div className="flex justify-center">
            <CheckboxInput
              register={register}
              registerName={`vehicles.${row.index}.applyVAT`}
              label=""
            />
          </div>
        ),
      },
      {
        header: "",
        id: "remove",
        cell: ({ row }: any) => (
          <button
            type="button"
            className="bg-red-500 hover:bg-red-600 text-white rounded p-2 flex items-center justify-center"
            onClick={() => remove(row.index)}
            aria-label="Remove Vehicle"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        ),
      },
    ],
    [register, remove, handleVrmLookup, isSearching, openAddressModal]
  );

  const table = useReactTable({
    data: vehicles,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <>
      <CreateVehicleModal 
        open={createVehicleOpen} 
        setOpen={setCreateVehicleOpen} 
        setMiscVehicle={setMiscVehicle} 
        handleVrmLookup={handleVrmLookup} 
        rowIndex={vehicles.length - 1} 
      />

      <table className="min-w-full divide-y divide-gray-300 border bg-white">
        <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id} className="bg-primary-50 text-gray-800">
              {headerGroup.headers.map(header => (
                <th key={header.id} className="py-2 px-3 text-sm font-semibold text-left">
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map(row => (
            <tr key={row.id} className="hover:bg-gray-100">
              {row.getVisibleCells().map(cell => (
                <td key={cell.id} className="px-3 py-2 border text-sm">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
} 